import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import Modal, {
  TextField,
  TextArea,
  SelectField,
  PrimaryButton,
  SecondaryButton,
} from "@/components/inventory/Modal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Stock view — per product, per warehouse (business rule #5).
 * Also handles the three movement forms: opening, adjustment, transfer.
 */
export default function StockView() {
  const { user } = useAuth();
  const canWrite = user && ["admin", "manager"].includes(user.role);

  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    product_id: "",
    warehouse_id: "",
    include_zero: false,
  });

  // Movement modals
  const [modal, setModal] = useState(null); // "opening" | "adjust" | "transfer" | null
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.include_zero) params.include_zero = true;
      const [sRes, pRes, wRes] = await Promise.all([
        api.get("/inventory/stock", { params }),
        api.get("/inventory/products", { params: { is_active: true, limit: 500 } }),
        api.get("/inventory/warehouses", { params: { is_active: true, limit: 500 } }),
      ]);
      setRows(sRes.data);
      setProducts(pRes.data);
      setWarehouses(wRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = (kind) => {
    if (kind === "opening") setForm({ product_id: "", warehouse_id: "", qty: "", notes: "" });
    if (kind === "adjust") setForm({ product_id: "", warehouse_id: "", direction: "in", qty: "", notes: "" });
    if (kind === "transfer") setForm({ product_id: "", from_warehouse_id: "", to_warehouse_id: "", qty: "", notes: "" });
    setModal(kind);
  };

  const submitOpening = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inventory/stock/opening", {
        product_id: form.product_id,
        warehouse_id: form.warehouse_id,
        qty: Number(form.qty),
        notes: form.notes || null,
      });
      toast.success("Opening stock recorded.");
      setModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inventory/stock/adjust", {
        product_id: form.product_id,
        warehouse_id: form.warehouse_id,
        direction: form.direction,
        qty: Number(form.qty),
        notes: form.notes || null,
      });
      toast.success("Adjustment recorded.");
      setModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inventory/stock/transfer", {
        product_id: form.product_id,
        from_warehouse_id: form.from_warehouse_id,
        to_warehouse_id: form.to_warehouse_id,
        qty: Number(form.qty),
        notes: form.notes || null,
      });
      toast.success("Transfer complete.");
      setModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <InventoryLayout
      title="Stock"
      right={
        canWrite && (
          <div className="flex gap-2">
            <SecondaryButton testid="stock-opening-btn" onClick={() => openModal("opening")}>Opening stock</SecondaryButton>
            <SecondaryButton testid="stock-adjust-btn" onClick={() => openModal("adjust")}>Adjust</SecondaryButton>
            <PrimaryButton testid="stock-transfer-btn" onClick={() => openModal("transfer")}>Transfer</PrimaryButton>
          </div>
        )
      }
    >
      {/* Filters */}
      <div className="mb-6 grid md:grid-cols-4 gap-3 items-end">
        <SelectField
          label="Product"
          testid="stock-filter-product"
          value={filters.product_id}
          onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}
        >
          <option value="">All products</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>
          ))}
        </SelectField>
        <SelectField
          label="Warehouse"
          testid="stock-filter-warehouse"
          value={filters.warehouse_id}
          onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
        >
          <option value="">All warehouses</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.code} · {w.name}</option>
          ))}
        </SelectField>
        <label className="flex items-center gap-2 h-11">
          <input
            data-testid="stock-filter-include-zero"
            type="checkbox"
            checked={filters.include_zero}
            onChange={(e) => setFilters({ ...filters, include_zero: e.target.checked })}
          />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            Include zero
          </span>
        </label>
        <SecondaryButton testid="stock-apply-filters" onClick={load}>Apply filters</SecondaryButton>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">SKU</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Product</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Warehouse</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 text-right">Quantity</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Updated</th>
            </tr>
          </thead>
          <tbody data-testid="stock-tbody">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">
                No stock rows. {canWrite && "Use \"Opening stock\" to record starting inventory."}
              </td></tr>
            ) : (
              rows.map((r) => (
                <tr key={`${r.product_id}-${r.warehouse_id}`} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-3 font-mono text-xs">{r.product_sku}</td>
                  <td className="px-4 py-3 font-medium">{r.product_name}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="font-mono text-xs mr-1">{r.warehouse_code}</span>· {r.warehouse_name}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-semibold ${r.quantity <= 0 ? "text-[#D92D20]" : ""}`}
                    data-testid={`stock-qty-${r.product_id}-${r.warehouse_id}`}
                  >
                    {r.quantity}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(r.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Opening stock modal */}
      <Modal
        testid="opening"
        open={modal === "opening"}
        onClose={() => setModal(null)}
        title="Opening stock"
      >
        <form onSubmit={submitOpening} className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            Records the starting quantity for a product in a warehouse. Only
            allowed once per pair — use adjustments for subsequent changes.
          </p>
          <SelectField
            label="Product"
            testid="opening-form-product"
            value={form.product_id}
            onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </SelectField>
          <SelectField
            label="Warehouse"
            testid="opening-form-warehouse"
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
          </SelectField>
          <TextField
            label="Quantity"
            testid="opening-form-qty"
            type="number"
            min="0"
            step="0.01"
            value={form.qty}
            onChange={(e) => setForm({ ...form, qty: e.target.value })}
            required
          />
          <TextArea
            label="Notes"
            testid="opening-form-notes"
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton testid="opening-form-cancel" type="button" onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton testid="opening-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Record opening"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* Adjust modal */}
      <Modal
        testid="adjust"
        open={modal === "adjust"}
        onClose={() => setModal(null)}
        title="Stock adjustment"
      >
        <form onSubmit={submitAdjust} className="space-y-4">
          <SelectField
            label="Product"
            testid="adjust-form-product"
            value={form.product_id}
            onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </SelectField>
          <SelectField
            label="Warehouse"
            testid="adjust-form-warehouse"
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
          </SelectField>
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Direction"
              testid="adjust-form-direction"
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value })}
              required
            >
              <option value="in">Increase (in)</option>
              <option value="out">Decrease (out)</option>
            </SelectField>
            <TextField
              label="Quantity"
              testid="adjust-form-qty"
              type="number"
              min="0.01"
              step="0.01"
              value={form.qty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
              required
            />
          </div>
          <TextArea
            label="Notes"
            testid="adjust-form-notes"
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton testid="adjust-form-cancel" type="button" onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton testid="adjust-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Record adjustment"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* Transfer modal */}
      <Modal
        testid="transfer"
        open={modal === "transfer"}
        onClose={() => setModal(null)}
        title="Stock transfer"
      >
        <form onSubmit={submitTransfer} className="space-y-4">
          <SelectField
            label="Product"
            testid="transfer-form-product"
            value={form.product_id}
            onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            required
          >
            <option value="">Select…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </SelectField>
          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="From warehouse"
              testid="transfer-form-from"
              value={form.from_warehouse_id}
              onChange={(e) => setForm({ ...form, from_warehouse_id: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </SelectField>
            <SelectField
              label="To warehouse"
              testid="transfer-form-to"
              value={form.to_warehouse_id}
              onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {warehouses
                .filter((w) => w.id !== form.from_warehouse_id)
                .map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </SelectField>
          </div>
          <TextField
            label="Quantity"
            testid="transfer-form-qty"
            type="number"
            min="0.01"
            step="0.01"
            value={form.qty}
            onChange={(e) => setForm({ ...form, qty: e.target.value })}
            required
          />
          <TextArea
            label="Notes"
            testid="transfer-form-notes"
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton testid="transfer-form-cancel" type="button" onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton testid="transfer-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Transferring…" : "Transfer"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </InventoryLayout>
  );
}
